import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/path';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { OrderValue, SortOrder, TableFiltersState, TableParams } from 'types/common';
import { useDidMount } from 'hooks/useDidMount';
import { BackdropLoader } from 'components/@shared/Loaders';
import TextField from '@mui/material/TextField';
import { ProposalResponseViewModel } from 'types/proposal';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { mainHeaderHeight } from 'theme/constants';
import { NoListYet } from 'components/@shared/NoListYet';
import { NoResultsFound } from 'components/@shared/NoResultsFound';
import { AdaptiveTable } from 'components/@shared/TableRemotePagination/AdaptiveTable';
import { ProposalsActionsMenu } from 'components/Proposals/List/ItemActionsMenu';
import { ProposalListRow } from 'components/Proposals/List/Row';
import { useModal } from 'hooks/useModal';
import { WarningPopUp } from 'components/@shared/WarningPopUp';
import { parametrizeURL } from 'routes/utils';
import { deleteProposal, getProposals } from 'services/proposalService';
import { ProposalsTableParams, State, initialProposalsTableParams, initialState, tableColumns } from './constants';
import { TableFilters } from 'components/@shared/TableFilters';
import { getFilterParams } from 'pages/Appointments/utils';
import { TableSwitch } from 'components/@shared/TableSwitch';
import { getHasFilterParams } from 'utils/getHasFilterParams';
import { ONLY_CONTRACTOR } from 'constants/roles';
import { useDeniedAccess } from 'hooks/useDeniedAccess';
import { ITableHeadCell } from 'components/@shared/TableRemotePagination/types';
import { ProposalListCell } from 'components/Proposals/List/Row/ProposalListCell';
import { Headline } from 'components/@shared/Headline';

export const ProposalsPage = () => {
    const [state, setState] = useState<State>(initialState);
    const [selectedItem, setSelectedItem] = useState({ name: '', id: '' });
    const navigate = useNavigate();

    const removeModal = useModal();

    const loadList = useCallback(
        (param: Partial<ProposalsTableParams>) => {
            const updatedParams: ProposalsTableParams = { ...state.tableParams, ...param };
            getProposals({ ...updatedParams, sortingOrder: Number(updatedParams.sortOrder === 'asc') as SortOrder })
                .then(({ items, count }) => {
                    setState((prevState) => ({
                        ...prevState,
                        isLoading: false,
                        list: items,
                        tableParams: updatedParams,
                        totalCount: count,
                    }));
                })
                .catch(() => {
                    setState((prevState) => ({ ...prevState, isLoading: false }));
                });
        },
        [state.tableParams],
    );

    const sortHandler = useCallback(
        (sortOrder: OrderValue, orderBy: string) => {
            loadList({ sortOrder, orderBy });
        },
        [loadList],
    );

    const setPageHandler = useCallback(
        (page: number) => {
            loadList({ page });
        },
        [loadList],
    );

    const setRowsPerPageHandler = useCallback(
        (pageSize: number) => {
            loadList({ page: 0, pageSize });
        },
        [loadList],
    );

    const searchChangeHandler = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        loadList({ page: 0, search: event.target.value });
    }, 500);

    const confirmDeleteHandler = useCallback(() => {
        removeModal.closeModal();
        setState((prevState) => ({ ...prevState, isLoading: true }));
        deleteProposal(selectedItem.id)
            .then(() => loadList(initialProposalsTableParams))
            .catch(() => {
                setState((prevState) => ({ ...prevState, isLoading: false }));
            });
    }, [state, removeModal, selectedItem]);

    const editHandler = useCallback(
        (id: string) => {
            navigate(parametrizeURL(PATH.EDIT_PROPOSAL, { id }));
        },
        [navigate],
    );

    const goToAppointmentHandler = useCallback(() => {
        navigate(PATH.APPOINTMENTS);
    }, [navigate]);

    const applyFiltersHandler = useCallback(
        (values: TableFiltersState) => {
            loadList({ page: 0, ...values });
        },
        [loadList],
    );

    const applyActiveStatusHandler = useCallback(
        (activeStatus: boolean) => {
            loadList({ page: 0, activeStatus });
        },
        [loadList],
    );

    const extraComponents = useMemo(
        () => ({
            Actions: ({ rowData }: { rowData: ProposalResponseViewModel }) => {
                const deleteHandler = (appointmentId: string) => {
                    setSelectedItem({ id: appointmentId, name: rowData.title });
                    removeModal.openModal();
                };

                return <ProposalsActionsMenu id={rowData.id} onDelete={deleteHandler} onEdit={editHandler} />;
            },
            Row: ({ rowData }: { rowData: ProposalResponseViewModel }) => (
                <ProposalListRow {...{ rowData, columns: tableColumns }} />
            ),
            Cell: ({ rowData, column }: { rowData: ProposalResponseViewModel; column: ITableHeadCell }) => (
                <ProposalListCell {...{ rowData, item: column }} />
            ),
        }),
        [removeModal, editHandler],
    );

    const filterParams = useMemo(() => getFilterParams(state.tableParams), [state.tableParams]);

    useDidMount(() => {
        loadList(state.tableParams);
    });

    useDeniedAccess(ONLY_CONTRACTOR);

    if (state.list.length === 0 && !state.isLoading && !getHasFilterParams(filterParams)) {
        return (
            <Box height={`calc(100vh - ${mainHeaderHeight}px)`}>
                <NoListYet
                    description="Go to appointments and create a new proposal"
                    buttonText="Go To Appointments"
                    onClick={goToAppointmentHandler}
                />
            </Box>
        );
    }

    return (
        <Box
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            px={2.5}
            py={2}
        >
            <Headline title="Your Proposals" />
            <Box width="100%">
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    mb={2}
                    gap={2}
                    rowGap={2}
                    flexWrap="wrap"
                >
                    <TextField onChange={searchChangeHandler} size="small" label="Search">
                        Search
                    </TextField>
                    <Box display="flex" alignItems="center" gap={2}>
                        <TableSwitch
                            isActive={state.tableParams.activeStatus}
                            label="Active"
                            onChange={applyActiveStatusHandler}
                        />
                        <TableFilters defaultValues={filterParams} onApplyFilters={applyFiltersHandler} />
                    </Box>
                </Box>
                {!!state.list.length && (
                    <AdaptiveTable
                        rows={state.list}
                        columns={tableColumns}
                        tableParams={state.tableParams}
                        totalCount={state.totalCount}
                        components={extraComponents}
                        onSort={sortHandler}
                        onSetPage={setPageHandler}
                        onSetRowsPerPage={setRowsPerPageHandler}
                    />
                )}
                {state.list.length === 0 && getHasFilterParams(filterParams) && <NoResultsFound />}
            </Box>
            {removeModal.open && (
                <WarningPopUp
                    isOpen={removeModal.open}
                    warningHeader="Remove Proposal?"
                    warningBody={`Are you sure you want to remove ${selectedItem.name} Proposal?`}
                    confirmButtonText="Remove"
                    onClose={removeModal.closeModal}
                    onConfirm={confirmDeleteHandler}
                />
            )}
            {state.isLoading && <BackdropLoader />}
        </Box>
    );
};
